import { useEffect, useRef, useState } from 'preact/hooks';
import {
  listMemories,
  listMemoryTopics,
  deleteMemory,
  optimizeMemories,
  describeApiError,
} from '../lib/api';
import { activeToken, activeDbId } from '../store/global';
import type { MemorySummary } from '../types/agno-api';
import { PageHeader } from '../components/PageHeader';
import { DataTable } from '../components/DataTable';
import { formatRelative, truncate } from '../lib/format';

export function Memories() {
  const [memories, setMemories] = useState<MemorySummary[]>([]);
  const [topics, setTopics] = useState<string[]>([]);
  const [topic, setTopic] = useState('');
  const [userFilter, setUserFilter] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Só a última `refresh()` escreve no estado (ver Sessions.tsx).
  const reqSeq = useRef(0);

  const refresh = async () => {
    if (!activeToken.value) return;
    const seq = ++reqSeq.current;
    const stale = () => seq !== reqSeq.current;
    setLoading(true);
    setError(null);
    try {
      // Sem filtro implícito por user_id — listamos TUDO. O filtro de usuário
      // agora é explícito (input em cima da tabela) pra qualquer agente, qualquer
      // canal. user_id no Agno depende do canal (telefone via Zapster, texto
      // livre via agents-chat), então filtrar default por um valor confunde mais
      // do que ajuda.
      const params: { user_id?: string; topic?: string } = {};
      if (userFilter.trim()) params.user_id = userFilter.trim();
      if (topic) params.topic = topic;
      const [m, t] = await Promise.all([
        listMemories(params),
        listMemoryTopics().catch(() => []),
      ]);
      if (stale()) return;
      const data = Array.isArray(m) ? m : (m as { data: MemorySummary[] }).data || [];
      setMemories(data);
      setTopics((t as string[]) || []);
    } catch (e) {
      if (stale()) return;
      console.error(e);
      setMemories([]);
      setError(describeApiError(e));
    } finally {
      if (!stale()) setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
  }, [activeToken.value, activeDbId.value, topic, userFilter]);

  const remove = async (id: string) => {
    if (!confirm('Apagar esta memória?')) return;
    await deleteMemory(id);
    refresh();
  };

  const optimize = async () => {
    if (!confirm('Otimizar memórias agora? Pode demorar e gerar custo.')) return;
    await optimizeMemories();
    setTimeout(refresh, 1500);
  };

  return (
    <div class="mx-auto max-w-7xl px-6 py-6">
      <PageHeader
        title="Memórias"
        description="Fatos persistidos sobre cada usuário. O agente consulta antes de responder."
        icon={<i class="ph ph-brain text-xl" />}
        actions={
          <>
            <select
              class="h-8 rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-xs focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50 focus-visible:border-ring disabled:opacity-50"
              value={topic}
              onChange={(e) => setTopic((e.target as HTMLSelectElement).value)}
            >
              <option value="">Todos os tópicos</option>
              {topics.map((t) => (
                <option value={t}>{t}</option>
              ))}
            </select>
            <button
              class="inline-flex items-center justify-center gap-2 h-8 px-4 rounded-md text-sm font-medium bg-background text-foreground border border-input shadow-xs hover:bg-accent hover:text-accent-foreground focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50 disabled:opacity-50 disabled:pointer-events-none"
              onClick={optimize}
              title="Otimizar memórias"
            >
              <i class="ph ph-sparkle text-base" /> Otimizar
            </button>
            <button
              class="inline-flex items-center justify-center gap-2 h-8 px-4 rounded-md text-sm font-medium bg-background text-foreground border border-input shadow-xs hover:bg-accent hover:text-accent-foreground focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50 disabled:opacity-50 disabled:pointer-events-none"
              onClick={refresh}
              disabled={loading}
            >
              {loading ? 'Atualizando…' : 'Atualizar'}
            </button>
          </>
        }
      />

      <UserFilter value={userFilter} onChange={setUserFilter} />

      {error && (
        <div class="mb-3 text-xs text-destructive font-mono border-l-2 border-destructive pl-2 break-words">
          Falha ao listar memórias: {error}
        </div>
      )}

      <DataTable
        rows={memories as unknown as Array<Record<string, unknown>>}
        rowKey={(r) => (r as unknown as MemorySummary).id}
        columns={[
          {
            key: 'memory',
            label: 'Memória',
            render: (r) => {
              const m = r as unknown as MemorySummary;
              return (
                <div>
                  <div>{truncate(m.memory, 200)}</div>
                  {m.topic && (
                    <span class="inline-flex items-center gap-1 mt-1 rounded-sm border border-border px-1.5 py-0.5 text-xs font-medium text-muted-foreground font-mono">
                      {m.topic}
                    </span>
                  )}
                </div>
              );
            },
          },
          {
            key: 'user',
            label: 'User ID',
            render: (r) => {
              const m = r as unknown as MemorySummary;
              const uid = m.user_id;
              if (!uid) return <span class="text-xs text-muted-foreground">—</span>;
              return (
                <button
                  class="font-mono tabular-nums text-xs text-muted-foreground hover:text-foreground hover:underline rounded-sm focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50"
                  onClick={() => setUserFilter(uid)}
                  title="Filtrar por este user_id"
                >
                  {uid}
                </button>
              );
            },
            width: '18ch',
          },
          {
            key: 'updated',
            label: 'Atualizado',
            render: (r) => <span class="text-xs">{formatRelative((r as unknown as MemorySummary).updated_at)}</span>,
            width: '14ch',
          },
          {
            key: 'actions',
            label: '',
            width: '5ch',
            render: (r) => (
              <button
                class="inline-flex items-center justify-center size-8 rounded-md text-muted-foreground hover:bg-destructive/10 hover:text-destructive focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-destructive/40"
                onClick={() => remove((r as unknown as MemorySummary).id)}
                title="Excluir memória"
                aria-label="Excluir memória"
              >
                <i class="ph ph-trash text-sm" />
              </button>
            ),
          },
        ]}
        empty={
          activeToken.value
            ? userFilter
              ? `Nenhuma memória para o user_id "${userFilter}".`
              : 'Nenhuma memória armazenada. O agente cria memórias conforme conversa com os usuários.'
            : 'Configure o token para ver as memórias.'
        }
      />
    </div>
  );
}

export function UserFilter({
  value,
  onChange,
}: {
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <div class="mb-3 flex items-center gap-2">
      <div class="relative flex-1 max-w-sm">
        <i class="ph ph-magnifying-glass text-sm absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
        <input
          class="h-8 w-full rounded-md border border-input bg-transparent pl-8 pr-8 py-1 text-sm font-mono shadow-xs placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50 focus-visible:border-ring disabled:opacity-50"
          placeholder="Filtrar por user_id (ex: 553488152574, chat:nome@blips.com.br)"
          value={value}
          onInput={(e) => onChange((e.target as HTMLInputElement).value)}
        />
        {value && (
          <button
            class="absolute right-1.5 top-1/2 -translate-y-1/2 inline-flex items-center justify-center size-5 rounded-sm text-muted-foreground hover:bg-accent focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50"
            onClick={() => onChange('')}
            title="Limpar filtro"
            aria-label="Limpar filtro"
          >
            <i class="ph ph-x text-xs" />
          </button>
        )}
      </div>
    </div>
  );
}
